import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { 
    FONT_SIZE_XS, 
    BUTTON_LETTER_SPACING, 
    BUTTON_FONT_FAMILY, 
    BUTTON_FONT_WEIGHT, 
    FONT_SIZE_4XS 
} from "../style/fontSettings";
import { 
    MELOGY_BLACK, 
    MELOGY_WHITE 
} from "../style/colors";
import { findFarthestColor, getStyleColor, getStyleBackgroundImage } from "../util/StyleSettingsUtil";
import ButtonWithIconOnTop from "./buttons/ButtonWithIconOnTop";
import Button from "./buttons/Button";
import { FaPlus, FaPen } from "react-icons/fa";

const StylesSectionContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    position: relative;
`;

const StylesHeader = styled.div`
    width: 100%;
    padding: 16px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ backgroundColor }) => backgroundColor || "transparent"};
    z-index: 1;
`;

const StylesHeaderContent = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 0 80px; /* Padding to account for side overlays */
    box-sizing: border-box;
`;

const StylesTitle = styled.div`
    font-family: ${BUTTON_FONT_FAMILY};
    font-weight: ${BUTTON_FONT_WEIGHT};
    font-size: ${FONT_SIZE_XS};
    color: ${MELOGY_WHITE};
    text-transform: uppercase;
    letter-spacing: ${BUTTON_LETTER_SPACING};
`;

const StylesContent = styled.div`
    width: 100%;
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
`;

const StylesBackground = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
`;

const StyledSwiperSlide = styled(SwiperSlide)`
    width: 120px !important;
    height: 100% !important;
    
    @media (min-width: 768px) {
        width: 160px !important;
    }
    
    @media (min-width: 1024px) {
        width: 200px !important;
    }
    
    @media (min-width: 1440px) {
        width: 240px !important;
    }
`;

const StyleContainers = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

const StyleCard = styled.div`
    height: 100%;
    width: 100%;
    background-color: ${({ backgroundColor }) => backgroundColor || "#000"};
    background-image: ${({ backgroundImage }) => backgroundImage ? `url(${backgroundImage})` : 'none'};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    cursor: ${({ hasStyle }) => (hasStyle ? "pointer" : "default")};
    padding: 16px;
    box-sizing: border-box;
    border-radius: 8px;
    position: relative;
    
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${({ backgroundColor }) => backgroundColor || "#000"};
        opacity: ${({ backgroundImage }) => backgroundImage ? 0 : 1};
        border-radius: 8px;
        z-index: 0;
        display: ${({ backgroundImage }) => backgroundImage ? 'none' : 'block'};
    }
    
    & > * {
        position: relative;
        z-index: 1;
    }
`;

const StyleNameContainer = styled.div`
    height: auto; 
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    overflow: hidden; 
    padding: 8px;
    background-color: rgba(0, 0, 0, 0.9);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border-radius: 4px;
`;

const StyleNameText = styled.div`
    font-size: clamp(${FONT_SIZE_4XS}, 2vw, ${FONT_SIZE_XS}); 
    font-family: ${BUTTON_FONT_FAMILY};
    font-weight: ${BUTTON_FONT_WEIGHT};
    letter-spacing: ${BUTTON_LETTER_SPACING};
    color: ${({ color }) => (color ? color : "#fff")};
    text-align: center;
    word-break: break-word; 
    white-space: normal;
    overflow-wrap: break-word; 
    display: block;
    width: 100%; 
    padding: 8px;
    background-color: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-radius: 0;
`;

const StyleHeader = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 8px;
    position: relative;
    min-height: 40px;
`;

const HeaderButtonsContainer = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 10;
    display: flex;
    gap: 8px;
    align-items: center;
`;

const EditButtonContainer = styled.div`
    z-index: 10;
`;

const CreateButtonWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 0;
`;

const StylesSection = ({ 
    styles, 
    createAndFetchMidi,
    setSelectedStyle,
    setStyleSettingsIsOpen,
    selectedStyle,
}) => {
    const swiperRef = useRef(null);

    const getStyleTextColor = (style) => {
        if (!style) return MELOGY_WHITE;
        // Always use white text for better readability with dark backgrounds
        return MELOGY_WHITE;
    };

    const isCreatingRef = useRef(false);
    const isEditingRef = useRef(false);

    const handleCreateWithStyle = (style) => {
        isCreatingRef.current = true;
        setSelectedStyle(style);
        createAndFetchMidi(style);
        // Reset the flag after a short delay to allow the state update to complete
        setTimeout(() => {
            isCreatingRef.current = false;
        }, 100);
    };

    const handleStyleClick = (style) => {
        isEditingRef.current = true;
        setSelectedStyle(style);
        setStyleSettingsIsOpen(true);
        // Reset the flag after a short delay to allow the state update to complete
        setTimeout(() => {
            isEditingRef.current = false;
        }, 100);
    };

    // Center the selected style when user changes it (but not when creating or editing)
    useEffect(() => {
        if (!selectedStyle || !swiperRef.current || isCreatingRef.current || isEditingRef.current) return;

        const selectedIndex = styles.findIndex(style => {
            const styleName1 = style?.find((item) => item.name === "styleName")?.value;
            const styleName2 = selectedStyle?.find((item) => item.name === "styleName")?.value;
            return styleName1 === styleName2;
        });
        if (selectedIndex !== -1 && selectedIndex !== swiperRef.current.activeIndex) {
            swiperRef.current.slideTo(selectedIndex, 300);
        }
    }, [selectedStyle, styles]);
    
        return (
            <StylesSectionContainer>
                <StylesHeader>
                    <StylesHeaderContent>
                        <StylesTitle>Styles</StylesTitle>
                    </StylesHeaderContent>
                </StylesHeader>
            <StylesContent>
                <StylesBackground>
                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        direction="horizontal"
                        spaceBetween={6}
                        slidesPerView="auto"
                        centeredSlides={true}
                        centeredSlidesBounds={false}
                        grabCursor={true}
                        watchSlidesProgress={true}
                        style={{
                            height: "100%",
                            width: "100%",
                        }}
                    >
                            {styles.map((style, index) => {
                                const styleObj = style;
                                const backgroundColor = getStyleColor(styleObj);
                                const backgroundImage = getStyleBackgroundImage(styleObj);
                                const textColor = getStyleTextColor(styleObj);
                                const styleNameValue = styleObj?.find((item) => item.name === "styleName")?.value || "";
                                
                                return (
                                    <StyledSwiperSlide key={index}>
                                        <StyleContainers>
                                        <StyleCard
                                            hasStyle={!!styleObj}
                                            backgroundColor={backgroundColor}
                                            backgroundImage={backgroundImage}
                                            onClick={() => {
                                                if (styleObj) {
                                                    handleStyleClick(styleObj);
                                                }
                                            }}
                                        >
                                            {styleObj && (
                                                <>
                                                    <StyleHeader>
                                                        <HeaderButtonsContainer>
                                                            <EditButtonContainer
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (styleObj) {
                                                                        handleStyleClick(styleObj);
                                                                    }
                                                                }}
                                                            >
                                                                <Button
                                                                    height="32px"
                                                                    width="32px"
                                                                    justifyContent="flex-start"
                                                                    buttonColor="white"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (styleObj) {
                                                                            handleStyleClick(styleObj);
                                                                        }
                                                                    }}
                                                                    icon={<FaPen style={{ fontSize: "16px" }} />}
                                                                />
                                                            </EditButtonContainer>
                                                        </HeaderButtonsContainer>
                                                    </StyleHeader>
                                                    <StyleNameContainer>
                                                        <StyleNameText color={textColor}>
                                                            {styleNameValue}
                                                        </StyleNameText>
                                                    </StyleNameContainer>
                                                    <CreateButtonWrapper
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (styleObj) {
                                                                handleCreateWithStyle(styleObj);
                                                            }
                                                        }}
                                                    >
                                                        <ButtonWithIconOnTop
                                                            text="Create"
                                                            buttonColor="white"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (styleObj) {
                                                                    handleCreateWithStyle(styleObj);
                                                                }
                                                            }}
                                                            icon={<FaPlus style={{ fontSize: "16px" }} />}
                                                            width="100px"
                                                            background="linear-gradient(177deg, rgba(249, 98, 249, 1) 2%, rgba(98, 249, 249, 1) 100%)"
                                                        />
                                                    </CreateButtonWrapper>
                                                </>
                                            )}
                                        </StyleCard>
                                        </StyleContainers>
                                    </StyledSwiperSlide>
                            );
                        })}
                    </Swiper>
                </StylesBackground>
            </StylesContent>
        </StylesSectionContainer>
    );
};

export default StylesSection;

